import { calculatePoints } from 'components/plateviewer/well/WellDrawingEvents';
import React, { useEffect, useState } from 'react';
import { Rect, Text, Group } from 'react-konva';

export interface CanvasTooltipProps {
  scale: number;
  toolTipText: string;
  stage?: any;
  windowPointerPositionX: number;
  windowPointerPositionY: number;
}

const getTextWidth = (lines: string[], customCanvas) => {
  let textWidth = 0;
  lines?.forEach((line) => {
    const stripped = line.replace('\u0001', '');
    const textMetrics = customCanvas.measureText(stripped);
    const lineWidth = textMetrics.width;
    if (lineWidth > textWidth) {
      textWidth = lineWidth;
    }
  });
  return textWidth;
};

export const CanvasTooltip = (props: CanvasTooltipProps) => {
  const { toolTipText, stage, windowPointerPositionX, windowPointerPositionY } = props;
  const scale = props.scale ?? 1;
  const customCanvas = document.createElement('canvas').getContext('2d');
  customCanvas.font = '12px Arial';
  const lines = toolTipText?.split('\n');
  const numLines = lines?.length;
  const textMetrics = customCanvas.measureText(lines ? lines[0] : '');
  const textWidth = getTextWidth(lines, customCanvas);
  const textHeight =
    textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;
  const toolTiplineHeight = textHeight * 1.2;
  const rectHeight = (toolTiplineHeight * (numLines * 1.5)) / scale;
  const [pos, setPos] = useState(null);
  // Get pointer position on the scaled canvas
  const pointerPos = stage?.getPointerPosition();

  useEffect(() => {
    if (!toolTipText) {
      return;
    }
    const centerX = window.innerWidth / 2; // provide x axis center in current window
    const centerY = window.innerHeight / 2; // provide y axis center in current window
    const position = calculatePoints(pointerPos, stage);
    if (windowPointerPositionX > centerX) {
      // decrease X axis by text width, if pointer poistion is at the right side of
      // screen more than 50% from center than show tooltip at left i.e, decrease x position
      position.x = position.x - textWidth / scale;
    }
    if (windowPointerPositionY > centerY) {
      // decrease Y axis by rect height, if pointer poistion is at the bottom side of
      // screen more than 50% from center than show tooltip at upper side i.e, decrease y position
      position.y = position.y - rectHeight - (scale > 5 ? 5 : 15); // adjust extra padding according to scale
    }
    setPos(position);
  }, [windowPointerPositionX, windowPointerPositionY]); // whenever pointer is moved inside canvas these properties are changed

  if (!pos || !toolTiplineHeight || !numLines) {
    return null;
  }

  return (
    //adjust padding according to scale
    <Group id="canvas-tooltip-group" x={pos.x} y={scale > 3 ? pos.y + 3 : pos.y + 10}>
      <Rect
        id="canvas-tooltip-rect"
        x={0}
        y={0}
        width={(textWidth + 30) / scale}
        height={rectHeight}
        fill="#F5F5F5"
        shadowBlur={5}
        shadowOffset={{ x: 5 / scale, y: 5 / scale }}
        shadowOpacity={0.5}
        cornerRadius={1}
      />
      {lines.map((line, i) => {
        const markerPos = line.indexOf('\u0001');
        const boldText = markerPos !== -1 ? line.slice(0, markerPos) : '';
        const normalText = markerPos !== -1 ? line.slice(markerPos + 1) : line;
        const boldWidth = boldText
          ? (() => {
              customCanvas.font = 'bold 12px Arial';
              const width = customCanvas.measureText(boldText).width / scale;
              customCanvas.font = '12px Arial';
              return width;
            })()
          : 0;
        const yPos = 0.5 + i * (toolTiplineHeight / scale) * 1.5;

        return (
          <Group key={i} y={yPos}>
            {boldText && (
              <Text
                id={`canvas-tooltip-text-bold-${toolTipText}`}
                text={boldText}
                x={0.5}
                y={0}
                fontSize={12 / scale}
                fontFamily="Arial"
                fontStyle="bold"
                fill="#000"
              />
            )}
            <Text
              id={`canvas-tooltip-text-${toolTipText}`}
              text={normalText}
              x={0.5 + boldWidth}
              y={0}
              fontSize={12 / scale}
              fontFamily="Arial"
              fill="#000"
            />
          </Group>
        );
      })}
    </Group>
  );
};
export default CanvasTooltip;
